import React from 'react';
import Icone, { NomeIcone } from './Icone';
import { useEstaSobreAMarca } from './FaixaDaMarca';

/**
 * Botão do design system (at-55, frente A).
 *
 * Existe para encerrar as **48 classes de botão** distintas medidas no CSS
 * legado em 08/09/2026 — entre elas `.right-button` e `.right-button2`,
 * `.ti-back-btn` e `.ti-back-btna` (um typo que virou classe viva). Também
 * substitui o `Button` do BlueprintJS, importado em 5 telas só por
 * `{ Button, Intent }` — a biblioteca inteira está no bundle por causa disso
 * (remoção é da frente D).
 *
 * ⚠️ Alvo de toque: `md` e `lg` têm no mínimo 44px de altura, exigência da
 * spec 01 §7 (o consultor usa o app na rua, com uma mão só).
 */

export type VarianteBotao = 'primario' | 'secundario' | 'perigo' | 'fantasma';
export type TamanhoBotao = 'sm' | 'md' | 'lg';

/*
 * ⚠️ **Cada variante declara a PRÓPRIA borda** — inclusive as que não têm, com
 * `tw-border-0`.
 *
 * 🔴 Antes o `tw-border-0` vivia na classe BASE, e isso **anulava** o `tw-border`
 * da variante secundária: são dois utilitários de mesma especificidade, então
 * quem vence é a ordem no CSS **gerado**, não a ordem da string — exatamente a
 * armadilha que o `Card.tsx` documenta. Resultado: a borda do botão secundário
 * simplesmente não aparecia (Victor, 09/09/2026: *"a borda do botão não está
 * aparecendo"*). Não repor `tw-border-0` na base.
 */
const POR_VARIANTE: Record<VarianteBotao, string> = {
  primario:
    'tw-border-0 tw-bg-brand tw-text-white hover:tw-bg-brand-deep active:tw-bg-brand-deep tw-shadow-sm',
  /*
   * ⚠️ A borda é CINZA MÉDIO, e tem sombra. Na at-55 era `superficie-borda`
   * (#E1E7E3) sem sombra, e sobre um painel branco o botão **desaparecia** —
   * lido por Victor em 09/09/2026 como "é branco no fundo branco, não parece um
   * botão" (o "Verificar no SGA", repetido em 10 linhas da tabela). Botão
   * secundário precisa de afordância própria, não emprestada do fundo.
   */
  secundario:
    'tw-border tw-border-texto-desabilitado/60 tw-bg-white tw-text-texto-corpo tw-shadow-sm hover:tw-border-brand hover:tw-bg-brand-tint hover:tw-text-brand',
  perigo: 'tw-border-0 tw-bg-danger tw-text-white hover:tw-brightness-90',
  fantasma:
    'tw-border-0 tw-bg-transparent tw-text-texto-suave hover:tw-text-brand hover:tw-bg-brand-tint',
};

const POR_TAMANHO: Record<TamanhoBotao, string> = {
  sm: 'tw-text-xs tw-px-3 tw-py-1.5 tw-gap-1.5 tw-rounded-lg tw-min-h-[32px]',
  /* 44px é o mínimo de alvo de toque da spec 01 §7 — não reduzir. */
  md: 'tw-text-sm tw-px-5 tw-py-2.5 tw-gap-2 tw-rounded-xl tw-min-h-[44px]',
  lg: 'tw-text-base tw-px-6 tw-py-3.5 tw-gap-2 tw-rounded-2xl tw-min-h-[52px]',
};

/**
 * 🔴 **Sobre a faixa LARANJA do cabeçalho o botão muda de pele** (at-66) — ver
 * `FaixaDaMarca.ts` para o porquê e para a origem no protótipo.
 *
 * Aparência única para `primario`, `secundario` e `fantasma`: branco translúcido, texto branco, sem
 * borda e sem sombra — a mesma família do hambúrguer e do voltar, que são `bg-white/15`. Aqui é
 * `/20` porque é **ação**, não navegação.
 *
 * ⚠️ **`perigo` fica de fora, de propósito:** ação destrutiva precisa continuar gritando, e
 * dissolvê-la no fundo seria perder o único sinal que ela tem. Não há caso hoje no app.
 */
const SOBRE_A_MARCA =
  'tw-border-0 tw-bg-white/20 tw-text-white tw-shadow-none hover:tw-bg-white/30 focus-visible:tw-ring-white/60 focus-visible:tw-ring-offset-0';

/**
 * Medidas do protótipo para a faixa (`px-3 py-2 text-xs`): a ação ali divide a linha com o gatilho
 * do menu, o voltar, o título e o sino.
 *
 * ⚠️ **É SUBSTITUIÇÃO, não sobreposição.** Passar `tw-px-3` por cima do `tw-px-5` do tamanho não
 * funcionaria: utilitários do Tailwind têm a mesma especificidade e quem vence é a ordem no CSS
 * **gerado** — a armadilha que o `Card.tsx` documenta. Trocando a string inteira, não há disputa.
 *
 * 📌 `min-h-[44px]` continua: o alvo de toque não encolhe (spec 01 §7). Só com ícone o botão fica
 * ~40×44, que é a medida dos controles redondos vizinhos.
 */
const POR_TAMANHO_SOBRE_A_MARCA: Record<TamanhoBotao, string> = {
  sm: 'tw-text-xs tw-px-2.5 tw-py-1.5 tw-gap-1.5 tw-rounded-lg tw-min-h-[32px]',
  md: 'tw-text-xs tw-px-3 tw-py-2 tw-gap-1.5 tw-rounded-xl tw-min-h-[44px]',
  lg: 'tw-text-sm tw-px-4 tw-py-2.5 tw-gap-2 tw-rounded-xl tw-min-h-[52px]',
};

interface PropsBotao extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variante?: VarianteBotao;
  tamanho?: TamanhoBotao;
  /** Mostra spinner e desabilita. O rótulo continua visível. */
  carregando?: boolean;
  iconeEsquerda?: NomeIcone;
  iconeDireita?: NomeIcone;
  /** Ocupa toda a largura disponível (padrão em formulário no mobile). */
  larguraTotal?: boolean;
}

const Botao: React.FC<PropsBotao> = ({
  variante = 'primario',
  tamanho = 'md',
  carregando = false,
  iconeEsquerda,
  iconeDireita,
  larguraTotal = false,
  disabled,
  className = '',
  children,
  ...resto
}) => {
  const desabilitado = disabled || carregando;

  /*
   * Dentro da faixa laranja do `PageHeader` o botão troca de pele — ver `SOBRE_A_MARCA`. Fora
   * dela o contexto é `false` e nada muda, então nenhuma outra tela é afetada.
   */
  const sobreAMarca = useEstaSobreAMarca() && variante !== 'perigo';

  return (
    <button
      /* `type="button"` por padrão: dentro de <form>, o default do HTML é
         "submit" e um botão auxiliar acabaria enviando o formulário. Quem
         precisa submeter passa `type="submit"` explicitamente. */
      type="button"
      disabled={desabilitado}
      /* Só anuncia ocupado enquanto carrega — leitor de tela avisa sem precisar
         de texto extra do tipo "Salvando...". */
      aria-busy={carregando || undefined}
      className={[
        /* Peso `medium` (500) na fonte de corpo (Nunito Sans) — o protótipo
           reserva a fonte da marca ao título da tela, e rótulo de botão em negrito
           pesa demais numa tabela onde ele se repete a cada linha (Victor,
           09/09/2026: "tire o texto do botão de negrito"). */
        'tw-inline-flex tw-items-center tw-justify-center tw-font-medium',
        'tw-cursor-pointer tw-select-none tw-transition-all',
        'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand/50 focus-visible:tw-ring-offset-2',
        'disabled:tw-cursor-not-allowed disabled:tw-opacity-60',
        sobreAMarca ? SOBRE_A_MARCA : POR_VARIANTE[variante],
        sobreAMarca ? POR_TAMANHO_SOBRE_A_MARCA[tamanho] : POR_TAMANHO[tamanho],
        larguraTotal ? 'tw-w-full' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...resto}
    >
      {carregando ? (
        <span
          className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-current tw-border-t-transparent"
          aria-hidden="true"
        />
      ) : (
        iconeEsquerda && <Icone nome={iconeEsquerda} tamanho={tamanho === 'sm' ? 14 : 16} />
      )}
      {children}
      {iconeDireita && !carregando && (
        <Icone nome={iconeDireita} tamanho={tamanho === 'sm' ? 14 : 16} />
      )}
    </button>
  );
};

export default Botao;
